import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Menu, MenuButton, IconButton, useToast } from '@chakra-ui/react'
import { FiMenu } from 'react-icons/fi'
import { CourseMenuList } from '../course/CourseMenuList'
import { ICourse } from '@/shared/ICourse'
import { DeleteCourseDialog } from '../course/DeleteCourseDialog'
import { EditCourseDialog } from '../dashboard/EditCourseDialog'
import { NewRepeatedCourseDialog } from '../dashboard/NewRepeatedCourseDialog'
import { AssignToExpertDialog } from './AssignToExpertDialog'
import { RemindProviderDialog } from '../dashboard/RemindProviderDialog'
import { EditDeadlineDialog } from '../deadlines/EditDeadlineDialog'
import { AuthService } from '../services/AuthService'
import { CourseStorageService } from '../services/CourseStorageService'
import { IUserInfo } from '@/shared/IUserInfo'
import { stringifyError } from '@/shared/parse'

export const CoursePageMenu = (props: {
  course?: ICourse;
  setCourse?: (course: ICourse) => void;
  isDisabled?: boolean;
}) => {

  const { course } = props;

  const navigate = useNavigate();
  const onCourseDeleted = async () => {
    navigate('/');
  }

  const onCourseUpdated = async (updated: ICourse) => {
    props.setCourse?.(updated);
  }

  const toast = useToast();
  const onSendReminder = async (course: ICourse, user: IUserInfo, message: string) => {
    await CourseStorageService.remindProvider(course.id, user, message);
    toast({
      title: 'Notification sent',
      description: `Message sent to ${user.name} (${user.email})`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    setIsRemindProviderDialogOpen(false);
  }

  const onRepeatedCourseCreated = async (created: ICourse) => {
    setIsNewRepeatedCourseDialogOpen(false);
    navigate(`/course/${created.id}`);
  }

  const onArchiveCourse = async (course: ICourse) => {
    const isArchiving = !course.archived;
    toast.promise(
      CourseStorageService.patchCourse(course.id, { archived: isArchiving }).then(updated => {
        props.setCourse?.(updated);
        return updated;
      }),
      {
        loading: { title: `${isArchiving ? 'Archiving' : 'Unarchiving'} course...` },
        success: (updated) => ({ 
          title: updated.archived ? 'Course archived' : 'Course unarchived',
          description: updated.archived ? 'The course has been archived successfully' : 'The course has been unarchived successfully'
        }),
        error: err => ({ 
          title: `Failed to ${isArchiving ? 'archive' : 'unarchive'} course`,
          description: stringifyError(err) 
        }),
      }
    );
  }

  const [isEditCourseDialogOpen, setIsEditCourseDialogOpen] = React.useState(false);
  const [isDeleteCourseDialogOpen, setIsDeleteCourseDialogOpen] = React.useState(false);
  const [isNewRepeatedCourseDialogOpen, setIsNewRepeatedCourseDialogOpen] = React.useState(false);
  const [isInviteExpertDialogOpen, setIsInviteExpertDialogOpen] = React.useState(false);
  const [isRemindProviderDialogOpen, setIsRemindProviderDialogOpen] = React.useState(false);
  const [isDeadlineDialogOpen, setIsDeadlineDialogOpen] = React.useState(false);

  return <>
    <Menu>
      <MenuButton className="no-print" as={IconButton} isDisabled={props.isDisabled || !course || !AuthService.isStaff} variant='ghost' icon={<FiMenu />}>Actions</MenuButton>
      {course && <CourseMenuList
        course={course}
        onDeleteCourse={() => setIsDeleteCourseDialogOpen(true)}
        onUpdateCourse={() => setIsEditCourseDialogOpen(true)}
        onInviteExpert={() => setIsInviteExpertDialogOpen(true)}
        onNewRepeatedCourse={() => setIsNewRepeatedCourseDialogOpen(true)}
        onRemindProvider={(_course) => setIsRemindProviderDialogOpen(true)}
        onCourseDeadlines={() => setIsDeadlineDialogOpen(true)}
        onArchiveCourse={onArchiveCourse}
      />}
    </Menu>
    {course && <>
      {isDeadlineDialogOpen && <EditDeadlineDialog course={course} onCourseDeadlinesChanged={onCourseUpdated} onClose={() => setIsDeadlineDialogOpen(false)} />}
      {isEditCourseDialogOpen && <EditCourseDialog course={course} onCourseUpdated={onCourseUpdated} onClose={() => setIsEditCourseDialogOpen(false)} />}
      {isDeleteCourseDialogOpen && <DeleteCourseDialog course={course} onCourseDeleted={onCourseDeleted} onClose={() => setIsDeleteCourseDialogOpen(false)} />}
      {isNewRepeatedCourseDialogOpen && <NewRepeatedCourseDialog course={course} onRepeatedCourseCreated={onRepeatedCourseCreated} onClose={() => setIsNewRepeatedCourseDialogOpen(false)} />}
      {isInviteExpertDialogOpen && <AssignToExpertDialog course={course} onClose={() => setIsInviteExpertDialogOpen(false)} onExpertChanged={onCourseUpdated} />}
      {isRemindProviderDialogOpen && <RemindProviderDialog course={course} onDismiss={() => setIsRemindProviderDialogOpen(false)} onSendReminder={onSendReminder} />}
    </>}
  </>
}